// Backend client for the playtest: passwordless email-code auth, cloud save, and
// feedback. Isolated here so the rest of the game stays offline-first; if the
// backend is unreachable the game still runs from localStorage.

import { t } from '@i18n/i18n';

export const API_BASE = 'https://gokturk-api.sait-askar.workers.dev';

// The worker returns stable machine codes; only whitelisted codes map to err.*
// dictionary keys (t() falls back key-to-itself, so unknown codes must not reach
// it or the player would see a raw 'err.foo'). Everything else -> generic.
const ERR_CODES = new Set([
  'invalid_email',
  'send_failed',
  'missing_fields',
  'need_code',
  'too_many_attempts',
  'code_expired',
  'code_wrong',
  'invalid_save',
  'empty_feedback',
  'auth_required',
  'google_failed',
]);
const TOKEN_KEY = 'gokturk.token';

let token: string | null = null;
try {
  token = localStorage.getItem(TOKEN_KEY);
} catch {
  /* ignore */
}

export function getToken(): string | null {
  return token;
}
export function setToken(t: string): void {
  token = t;
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch {
    /* ignore */
  }
}
export function clearToken(): void {
  token = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

interface ApiError extends Error {
  status?: number;
}

async function call(path: string, method: 'GET' | 'POST', body?: unknown, auth = false): Promise<any> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth && token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(API_BASE + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = ERR_CODES.has(data.error) ? t('err.' + data.error) : t('login.errGeneric');
    const err: ApiError = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export interface VerifyResult {
  token: string;
  email: string;
  name: string | null;
  save: string | null;
}

export interface SaveMeta {
  version: string;
  gameTime: number;
  planet: number;
  bestWave: number;
}

export const api = {
  requestCode: (email: string): Promise<{ ok: boolean; devCode?: string }> =>
    call('/auth/request', 'POST', { email }),
  verify: (email: string, code: string): Promise<VerifyResult> =>
    call('/auth/verify', 'POST', { email, code }),
  authConfig: (): Promise<{ google: boolean; googleClientId: string | null }> =>
    call('/auth/config', 'GET'),
  google: (credential: string): Promise<VerifyResult> =>
    call('/auth/google', 'POST', { credential }),
  load: (): Promise<{ save: string | null }> => call('/load', 'GET', undefined, true),
  save: (blob: string, meta: SaveMeta): Promise<{ ok: boolean }> =>
    call('/save', 'POST', { blob, meta }, true),
  feedback: (text: string, meta: unknown): Promise<{ ok: boolean }> =>
    call('/feedback', 'POST', { text, meta }, true),
};
